import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiSearch, FiCheck } from "react-icons/fi";
import "./SearchableSelect.css";

function getOptionsFromChildren(children) {
  return React.Children.toArray(children)
    .filter((child) => React.isValidElement(child) && child.type === "option")
    .map((child) => ({
      value: child.props.value,
      label: child.props.children,
      disabled: !!child.props.disabled,
    }));
}

// Drop-in replacement for a native <select>: pass the same value/onChange/
// disabled/className props and <option> children, get a searchable,
// fixed-height, scrollable dropdown panel instead of the native list.
export default function SearchableSelect({
  value,
  onChange,
  children,
  className = "",
  disabled = false,
  searchable = true,
  searchPlaceholder = "Search...",
  placeholder = "Select...",
  name,
  id,
}) {
  const options = useMemo(() => getOptionsFromChildren(children), [children]);
  const shouldSearch = searchable;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    if (shouldSearch) {
      const raf = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [open, shouldSearch]);

  // Mirror native <select> behavior: if the controlled value doesn't match
  // any option (e.g. a state default that isn't one of the option values),
  // the browser silently displays the first option rather than showing
  // nothing - so fall back to it here too instead of the placeholder.
  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) ?? options[0];

  const filteredOptions =
    shouldSearch && search
      ? options.filter((opt) =>
          String(opt.label ?? "")
            .toLowerCase()
            .includes(search.toLowerCase())
        )
      : options;

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    setOpen(false);
    onChange?.({ target: { value: opt.value, name } });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div
      className={`searchable-select${
        disabled ? " searchable-select--disabled" : ""
      }${open ? " searchable-select--open" : ""}`}
      ref={rootRef}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        id={id}
        className={`searchable-select__trigger${className ? ` ${className}` : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="searchable-select__value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FiChevronDown className="searchable-select__chevron" />
      </button>

      {open && (
        <div className="searchable-select__panel">
          {shouldSearch && (
            <div className="searchable-select__search">
              <FiSearch className="searchable-select__search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          <ul className="searchable-select__list" role="listbox">
            {filteredOptions.length === 0 ? (
              <li className="searchable-select__empty">No results found</li>
            ) : (
              filteredOptions.map((opt, idx) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <li
                    key={`${opt.value}-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    className={`searchable-select__option${
                      isSelected ? " searchable-select__option--selected" : ""
                    }${opt.disabled ? " searchable-select__option--disabled" : ""}`}
                    onClick={() => handleSelect(opt)}
                  >
                    <span>{opt.label}</span>
                    {isSelected && (
                      <FiCheck className="searchable-select__check" />
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
